
import Vue, { defineComponent, ref, onMounted, getCurrentInstance, onBeforeMount } from 'vue';
import VantList from 'vant/lib/list';
// eslint-disable-next-line import/no-extraneous-dependencies
import chunk from 'lodash/chunk';
import cookies from 'js-cookie';
import LogisticsIcon from './images/logistic-icon.png';
import { ECommerceApi, ECommerceGoods } from '@/api/e-commerce';
import { setCommonParamsCookiesInServer } from '@/common/common-params';
import ECommerceHeader from './components/header/index.vue';
import { header, extractTitleFromMetaInfo, getDefaultTitle } from '@/common/seo';
import { commonPvParams } from '@/common/utils';
import { immediateSendLog, reportFMP, sendLog } from '@/common/radar';

const ChunkLength = 2;

/**
 * @zh gtag 访问访问埋点，for 运营
 */
const gtagViewPageSize = () => {
    try {
        (window as any)?.gtag?.('event', 'conversion', {
            send_to: 'AW-940131115/MXGOCNnohqQaEKuGpcAD',
            value: 1.0,
            currency: 'CNY',
            transaction_id: '',
        });
    } catch {
        // do nothing
    }
};

/**
 * @TODO
 * 1、适配逻辑
 * 2、接口 seo tdk 相关
 * 3、商详和订单页的跳转
 * 4、埋点  https://track-analysis-sgp.corp.kuaishou.com/track/group/list?funcType=DEFAULT&appName=TRINITY&model.currentPage=1&model.pageSize=10&groupId=9248&model.pageType=0&model.bizName=GENERAL&tagModel.currentPage=1&tagModel.pageSize=10&viewMode=SIMPLE&groupName=20250227-%E7%94%B5%E5%95%86%E6%90%9C%E7%B4%A2%E7%BB%93%E6%9E%9C%E9%A1%B5
 */
const ElectronicCommerceSearchResult = defineComponent({
    components: {
        VantList,
        ECommerceHeader,
    },
    layout: 'e-commerce',

    setup() {
        const { proxy } = (getCurrentInstance() || {}) as {
            proxy: Vue & {
                goodsListInNode?: Array<ECommerceGoods>;
                pcursorInNode?: string;
            };
        };
        const goodsList = ref<Array<ECommerceGoods>>([]);
        const pcursor = ref<undefined | string>(undefined);
        const loading = ref(false);
        const finished = ref(false);

        const sendPv = () => {
            const params = commonPvParams({ defaultPageSource: '' });
            const country = proxy?.$store?.state?.startup?.countryInfo;
            const user_id = cookies.get('user_id');

            immediateSendLog({
                type: 'pv',
                name: 'ECOMMERCE_SEARCH_RESULTS_PAGE',
                value: {
                    country,
                    user_id,
                    ...params,
                    ...proxy?.$route.query,
                },
            });
        };

        const fetchGoodsList = async () => {
            try {
                loading.value = true;
                const res = await ECommerceApi.getECommerceSearchList({
                    keyword: proxy?.$route?.params?.searchWord || '',
                    count: 12,
                    pcursor: pcursor.value,
                    fromUser: proxy?.$route?.query?.is_user_search === 'true',
                    needLdJson: false,
                    needTdk: false,
                });
                const newGoodsList = res?.goods || [];
                goodsList.value = [...goodsList.value, ...newGoodsList];
                pcursor.value = newGoodsList[newGoodsList.length - 1]?.encryptItemId;
                loading.value = false;
                finished.value = newGoodsList.length <= 1;
            } catch {
                loading.value = false;
                finished.value = true;
            }
        };

        const handleClickSearchBtn = (value?: string) => {
            if (!proxy?.$route?.params?.searchWord && !value) {
                return;
            }

            proxy?.$router?.replace?.({
                path: `/eshop/${value}`,
                query: {
                    ...(proxy?.$route?.query || {}),
                    is_user_search: 'true',
                },
            });
        };

        const handleViewGoodsDetail = (goodsInfo: ECommerceGoods & { index?: number }) => {
            /**
             * @zh 本地环境测试访问 https://kwai-pwa-hzs.test.gifshow.com
             */
            window.open(`/shop/detail/${goodsInfo.encryptItemId}?keyword=${proxy?.$route?.params?.searchWord || ''}`, '_blank');

            sendLog({
                type: 'click',
                name: 'ECOMMERCE_PRODUCT_CARD',
                value: {
                    product_id: goodsInfo.encryptItemId,
                    index: goodsInfo.index,
                },
            });
        };

        onBeforeMount(() => {
            goodsList.value = [...goodsList.value, ...(proxy?.goodsListInNode || [])];
            pcursor.value = proxy?.pcursorInNode;
        });

        onMounted(() => {
            gtagViewPageSize();
            reportFMP();
            sendPv();
        });

        return {
            chunkLength: ChunkLength,
            loading,
            finished,
            goodsList,
            LogisticsIcon,
            fetchGoodsList,
            chunk,
            handleClickSearchBtn,
            handleViewGoodsDetail,
        };
    },
    async asyncData(ctx) {
        /**
         * @zh for 电商，网站所有权声明
         */
        const VerificationDeclareMeta = {
            name: 'google-site-verification',
            content: 'rIwBZPHFnnd1aSxU5NiLXs38KlrrLCDpwGTB8iC-nRU',
        };
        const DefaultAsyncData = {
            seoData: [],
            metaInfoData: [VerificationDeclareMeta],
            title: getDefaultTitle(ctx),
            goodsListInNode: [],
            pcursorInNode: undefined,
        };
        const url = `https://${ctx?.req?.headers?.host}${ctx?.route?.path}` || '';
        if (process.server) {
            setCommonParamsCookiesInServer({
                bucket: ctx.store.state.startup.bucket,
                host: ctx.req.headers.host,
                ctx,
                countryInfo: ctx.store.state.startup.countryInfo,
            });
            try {
                const res = await ECommerceApi.getECommerceSearchList({
                    keyword: ctx?.route?.params?.searchWord || '',
                    count: 12,
                    fromUser: false,
                    needLdJson: true,
                    needTdk: true,
                    url,
                });
                const goodsListInNode = res?.goods || [];

                return {
                    goodsListInNode,
                    pcursorInNode: goodsListInNode[goodsListInNode.length - 1]?.encryptItemId,
                    seoData: res?.ldJson || [],
                    metaInfoData: [VerificationDeclareMeta, ...(res?.tdk || [])],
                    title: extractTitleFromMetaInfo({
                        ctx,
                        metaInfoData: res?.tdk || [],
                    }),
                };
            } catch {
                return {
                    ...DefaultAsyncData,
                };
            }
        }

        return {
            ...DefaultAsyncData,
        };
    },
    head() {
        const head = header(this) as any;
        try {
            head.script.push(
                {
                    src: 'https://www.googletagmanager.com/gtag/js?id=AW-940131115',
                    crossorigin: 'anonymous',
                    async: true,
                },
                {
                    innerHTML: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag() {
                            dataLayer.push(arguments);
                        }
                        gtag('js', new Date());

                        gtag('config', 'AW-940131115');
                    `,
                }
            );
        } catch {
            // do nothing
        }
        return {
            ...head,
        };
    },
});

export default ElectronicCommerceSearchResult;
